import { Link } from 'react-router-dom';
import type { RunSummary } from '../state/runStore';
import { jiraIssueUrl, useJiraHost } from '../lib/jiraLinks';
import { TrainingBadge } from '../components/TrainingBadge';

interface Props {
  run: RunSummary;
}

interface PillProps {
  label: string;
  value: string;
  href?: string;
  mono?: boolean;
}

function Pill({ label, value, href, mono }: PillProps) {
  const inner = (
    <>
      <span className="context-pill-label">{label}</span>
      <span className={`context-pill-value ${mono ? 'mono' : ''}`}>{value}</span>
    </>
  );
  if (href) {
    return (
      <a
        className="context-pill jira-link"
        href={href}
        target="_blank"
        rel="noreferrer"
        title={`Open ${value} in Jira`}
      >
        {inner}
      </a>
    );
  }
  return <span className="context-pill">{inner}</span>;
}

/**
 * Sticky top bar shown on every dashboard panel. Renders the run identity
 * (game / studio / SR / TEXEC) plus a back link to the runs list. Mirrors the
 * Sneaky Project "Project / Game / Studio / Tester" header in the mockup.
 */
export function ContextTopBar({ run }: Props) {
  const host = useJiraHost();
  const srHref = jiraIssueUrl(host, run.sr_key) ?? undefined;
  const texecHref = jiraIssueUrl(host, run.texec_key) ?? undefined;
  return (
    <header className="context-topbar">
      <div className="context-topbar-left">
        <Link to="/" className="btn btn-ghost small">
          {'\u2190 Runs'}
        </Link>
        <h1 className="context-title">{run.game_name || run.texec_key}</h1>
      </div>
      <div className="context-topbar-pills">
        <Pill label="Studio" value={run.studio} />
        <Pill label="SR" value={run.sr_key} href={srHref} mono />
        <Pill label="TEXEC" value={run.texec_key} href={texecHref} mono />
        {/* Cross-family runs are impossible (TEXEC is the SR's child), so a
            single badge covers the whole run identity. */}
        <TrainingBadge keyValue={run.sr_key} />
      </div>
      <div className="context-topbar-right">
        <Link className="btn btn-ghost small" to="/settings">
          Settings
        </Link>
      </div>
    </header>
  );
}
